import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Facebook, Linkedin, Mail, MessageCircle, Twitter } from 'lucide-react'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import { listDepartments } from '@/features/employees/api'
import { JobPostingFormDialog } from '@/features/recruitment/JobPostingFormDialog'
import {
  JOB_POSTING_STATUSES,
  JOB_POSTING_STATUS_LABELS,
  deleteJobPosting,
  exportJobPostings,
  listJobPostings,
  type JobPostingFilters,
  type JobPostingRead,
  type JobPostingStatus,
} from '@/features/recruitment/api'
import { SHARE_TARGET_LABELS, shareUrlFor, type ShareTarget } from '@/features/recruitment/share'
import { toast } from '@/lib/toast'

const SHARE_ICONS: Record<ShareTarget, typeof Facebook> = {
  facebook: Facebook,
  linkedin: Linkedin,
  twitter: Twitter,
  whatsapp: MessageCircle,
  email: Mail,
}

/** Opens the platform's own compose screen for this posting -- see `features/recruitment/share.ts`. */
function SharePostingButtons({ posting }: { posting: JobPostingRead }) {
  return (
    <div className="flex items-center justify-end gap-0.5">
      {(Object.keys(SHARE_ICONS) as ShareTarget[]).map((target) => {
        const Icon = SHARE_ICONS[target]
        return (
          <Button
            key={target}
            type="button"
            variant="ghost"
            size="sm"
            title={SHARE_TARGET_LABELS[target]}
            aria-label={SHARE_TARGET_LABELS[target]}
            onClick={() => window.open(shareUrlFor(target, posting), '_blank', 'noopener,noreferrer')}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </Button>
        )
      })}
    </div>
  )
}

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUS_TONE: Record<JobPostingStatus, string> = {
  open: 'bg-success/10 text-success',
  on_hold: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  closed: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
}

/**
 * Every job posting open across the company, and where each one stands.
 *
 * Names lead to an edit dialog rather than a detail page -- the same shape `Employees`
 * uses for its own master list -- since a posting has no drill-down behind it; the
 * candidates against it live on the `Candidates` and `Applications` screens instead.
 */
export function JobPostings() {
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<JobPostingFilters>({ page: 1 })
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState<{ posting?: JobPostingRead } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<JobPostingRead | null>(null)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['job-postings', filters],
    queryFn: () => listJobPostings(filters),
    retry: false,
  })

  const departments = useQuery({
    queryKey: ['departments', ''],
    queryFn: () => listDepartments(),
    retry: false,
    staleTime: 5 * 60_000,
  })

  const canCreate = can('performance_reviews', 'create')
  const canUpdate = can('performance_reviews', 'update')
  const canDelete = can('performance_reviews', 'delete')

  const remove = useMutation({
    mutationFn: (posting: JobPostingRead) => deleteJobPosting(posting.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['job-postings'] })
      toast.success('Job posting removed.')
      setConfirmDelete(null)
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
      )
      setConfirmDelete(null)
    },
  })

  const postings = data?.data ?? []
  const meta = data?.meta

  const set = (patch: Partial<JobPostingFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  const columns: Column<JobPostingRead>[] = [
    {
      key: 'title',
      header: 'Title',
      className: 'font-medium',
      sortValue: (posting) => posting.title,
      cell: (posting) =>
        canUpdate ? (
          <button
            type="button"
            onClick={() => setDialog({ posting })}
            className="text-left hover:underline"
          >
            {posting.title}
          </button>
        ) : (
          posting.title
        ),
    },
    {
      key: 'department_name',
      header: 'Department',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (posting) => posting.department_name ?? '—',
    },
    {
      key: 'status',
      header: 'Status',
      cell: (posting) => (
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[posting.status]}`}>
          {JOB_POSTING_STATUS_LABELS[posting.status]}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Opened',
      className: 'whitespace-nowrap text-slate-500 dark:text-slate-400',
      sortValue: (posting) => posting.created_at,
      cell: (posting) => new Date(posting.created_at).toLocaleDateString(),
    },
    {
      key: 'share',
      header: 'Share',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (posting) =>
        posting.status === 'open' ? (
          <SharePostingButtons posting={posting} />
        ) : (
          <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
        ),
    },
    ...(canDelete
      ? [
          {
            key: 'actions',
            header: '',
            headerClassName: 'text-right',
            className: 'text-right',
            cell: (posting: JobPostingRead) => (
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(posting)}>
                Delete
              </Button>
            ),
          } satisfies Column<JobPostingRead>,
        ]
      : []),
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Job postings</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} posting${meta.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        {canCreate && <Button onClick={() => setDialog({})}>New job posting</Button>}
      </header>

      <Card>
        <CardHeader title="Postings" />
        <DataTable
          rows={postings}
          columns={columns}
          getRowId={(posting) => posting.id}
          caption="Job postings"
          isLoading={isLoading}
          error={error}
          emptyMessage="No job postings match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="posting-search">
                Search job postings
              </label>
              <input
                id="posting-search"
                className={SELECT_CLASS}
                placeholder="Title…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') set({ q: search })
                }}
                onBlur={() => set({ q: search })}
              />

              <label className="sr-only" htmlFor="posting-department">
                Department
              </label>
              <select
                id="posting-department"
                className={SELECT_CLASS}
                value={filters.department_id ?? ''}
                onChange={(event) => set({ department_id: event.target.value || undefined })}
              >
                <option value="">Any department</option>
                {(departments.data?.data ?? []).map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="posting-status">
                Status
              </label>
              <select
                id="posting-status"
                className={SELECT_CLASS}
                value={filters.status ?? ''}
                onChange={(event) => set({ status: event.target.value as JobPostingStatus | '' })}
              >
                <option value="">Any status</option>
                {JOB_POSTING_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {JOB_POSTING_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>

              {(filters.q || filters.department_id || filters.status) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch('')
                    setFilters({ page: 1 })
                  }}
                >
                  Clear
                </Button>
              )}

              <ExportButton
                filename="job-postings"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportJobPostings(filters, format)}
                errorMessage="Could not export the postings."
              />
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>

      {dialog && (
        <JobPostingFormDialog open posting={dialog.posting} onClose={() => setDialog(null)} />
      )}

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Delete ${confirmDelete.title}?` : 'Delete job posting'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete)}
            >
              Delete
            </Button>
          </>
        }
      >
        <Alert tone="info">
          This is a soft delete: the posting is hidden from this list, but every application
          against it stays exactly as it was.
        </Alert>
      </Modal>
    </div>
  )
}
